/**
 * Os endereços da vitrine, e as perguntas sobre eles.
 *
 * A vitrine mora na RAIZ. Só o self-service (`/auto-servico/:code`) e o
 * catálogo de kits (`/catalogo/:slug`) têm namespace próprio.
 *
 * **Não existe mais lista de slugs reservados**, e a ausência é a metade boa
 * desta mudança. Enquanto a marca era `/:slug`, todo segmento estático da
 * vitrine competia com ela, e uma marca chamada "Pedidos" ficava inalcançável
 * em silêncio — a lista existia para conter isso, e falhou na primeira rota
 * nova que alguém esqueceu de acrescentar nela.
 *
 * Com `/marca/:slug` **não há segmento dinâmico na raiz**. A colisão não foi
 * movida para outro lugar: ela deixou de ser possível.
 */

/** A home da vitrine. */
export const HOME_PATH = '/';

/** Onde a operação mora, para quem alterna a experiência. */
export const OPERATION_PATH = '/operacao';

/**
 * O namespace da marca.
 *
 * A palavra a mais na URL é o preço de não ter lista de reservadas — e é
 * barato: sem ela, acrescentar uma rota estática à vitrine exigiria lembrar de
 * um segundo lugar, para sempre.
 */
export const BRAND_BASE = '/marca';

export function brandPath(slug: string): string {
  return `${BRAND_BASE}/${slug}`;
}

export function categoryPath(slug: string, brandSlug?: string | null): string {
  return brandSlug
    ? `${BRAND_BASE}/${brandSlug}/${slug}`
    : `${CATALOG_ALL_PATH}/${slug}`;
}

export function productPath(productId: string): string {
  return `/produto/${encodeURIComponent(productId)}`;
}

/**
 * A rota do carrinho.
 *
 * **Fora do fluxo, e de propósito — não é código morto.** O botão do cabeçalho
 * passou a abrir a gaveta, então nada mais LINKA para cá; a rota continua de pé
 * para link direto, histórico e compartilhamento, e porque a decisão de
 * aposentar a página ainda não foi tomada.
 *
 * Quem encontrar isto sem referência de entrada: não apague antes de confirmar
 * que a página saiu de vez.
 */
export const CART_PATH = '/carrinho';

/**
 * A listagem completa, com busca e filtros.
 *
 * Ela morava na home até a curadoria ocupar a raiz. O segmento `categorias`
 * serve às duas — a listagem inteira e o recorte por categoria — em vez de
 * inventar um `todas`.
 */
export const CATALOG_ALL_PATH = '/categorias';

/**
 * A vitrine do estoque próprio. Rota estática na raiz, sem competir com nada:
 * a marca tem namespace (`/marca`).
 */
export const READY_DELIVERY_PATH = '/pronta-entrega';

/**
 * As páginas de campanha. Namespace próprio, como a marca — e por aqui o
 * motivo é outro: o slug da campanha é escolhido no manager, e sem namespace
 * uma campanha chamada "pedidos" ficaria inalcançável em silêncio.
 *
 * **Não há entrada no menu nem na home**: a campanha existe pelo link do
 * disparo, e é ele que define quem a vê. Por isso também **não existe um
 * `campaignPath()`** aqui ao lado dos outros: nada no clearance linka para uma
 * campanha, e a URL que o time divulga é montada pelo manager, com domínio
 * (`public_url_for`, na surplus-api). O que se pergunta aqui é só "este caminho
 * é de campanha?", e para isso basta a base.
 */
export const CAMPAIGN_BASE = '/campanhas';

/** Meus pedidos. */
export const ORDERS_PATH = '/pedidos';

export function orderPath(orderId: string): string {
  return `${ORDERS_PATH}/${encodeURIComponent(orderId)}`;
}

/**
 * Os prefixos que **não** são vitrine.
 *
 * Com a vitrine na raiz, "estou nela?" deixou de ser um prefixo e virou a
 * ausência dos outros — todo caminho começa com `/`.
 *
 * A lista é frágil pelo mesmo motivo que a de reservadas era: uma rota nova
 * fora da vitrine precisa entrar aqui. Mas o custo de esquecer mudou de
 * natureza. Antes, uma marca sumia da loja sem erro nenhum; agora, o cabeçalho
 * da vitrine aparece numa tela que não é dela — visível na primeira olhada.
 */
const FORA_DA_VITRINE = [
  '/login',
  '/auto-servico',
  '/self-service',
  '/catalogo',
  OPERATION_PATH,
] as const;

/** Este caminho é da vitrine? */
export function isStorefrontPath(pathname: string): boolean {
  return !FORA_DA_VITRINE.some(
    (prefixo) => pathname === prefixo || pathname.startsWith(`${prefixo}/`)
  );
}

/** É a home da vitrine — e não uma tela dentro dela. */
export function isStorefrontHome(pathname: string): boolean {
  return pathname.replace(/\/$/, '') === '';
}

export interface StorefrontRouteSlugs {
  brandSlug: string | null;
  categorySlug: string | null;
}

/**
 * Marca e categoria do caminho, lidos do `pathname`.
 *
 * O menu de categorias mora no LAYOUT, e `useParams()` num layout devolve só o
 * que a própria rota casou — não os parâmetros do filho. Ler o caminho é a
 * leitura honesta.
 *
 * Ficou trivial: `/marca/x/y` dá marca e categoria, `/categorias/y` dá só a
 * categoria, e **qualquer outra coisa não é nem uma nem outra**. Enquanto a
 * marca era `/:slug`, este era o ponto em que um segmento estático esquecido
 * virava "a marca `pedidos`".
 */
export function parseStorefrontPath(pathname: string): StorefrontRouteSlugs {
  const [primeiro, segundo, terceiro] = pathname
    .split('/')
    .filter((segmento) => segmento.length > 0);

  if (primeiro === BRAND_BASE.slice(1)) {
    return { brandSlug: segundo ?? null, categorySlug: terceiro ?? null };
  }
  if (primeiro === CATALOG_ALL_PATH.slice(1)) {
    return { brandSlug: null, categorySlug: segundo ?? null };
  }
  return { brandSlug: null, categorySlug: null };
}
